/**
 * Form admin pages generator: creates admin UI for viewing/managing form submissions
 *
 * Generates under (authenticated)/forms/<name>/:
 *   page.tsx, columns.tsx, actions.tsx, <name>-submissions-table.tsx,
 *   <name>-submissions-page-content.tsx, <name>-submission-drawer.tsx
 */

import {
  getAllFormSchemaFields,
  hasDynamicFields
} from '@core-engine/schema/field-helpers/index.js'
import type { FormSchema, GeneratedFile, GeneratorOptions } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toCamelCase, toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import { buildFormDevModeIntegration } from '@next-adapter/generators/dev-mode-integration.js'
import { generateColumns } from '@next-adapter/generators/form-admin/admin-columns.js'
import {
  generateFormDeleteDialogFile,
  generateFormRowActionsFile
} from '@next-adapter/generators/form-admin/admin-delete-action.js'
import {
  generatePage,
  generatePageSkeleton
} from '@next-adapter/generators/form-admin/admin-page.js'
import { generatePageContent } from '@next-adapter/generators/form-admin/admin-page-content.js'
import { generateSubmissionDrawer } from '@next-adapter/generators/form-admin/admin-submission-drawer.js'
import { generateTable } from '@next-adapter/generators/form-admin/admin-table.js'

// ============================================================================
// Result Type
// ============================================================================

export interface FormAdminPagesResult {
  files: GeneratedFile[]
}

// ============================================================================
// Generator
// ============================================================================

export function generateFormAdminPages(
  schema: FormSchema,
  pagesDir: string,
  options: GeneratorOptions
): FormAdminPagesResult {
  const formName = schema.name
  const kebab = toKebabCase(formName)
  const pascal = toPascalCase(formName)
  const camel = toCamelCase(formName)
  const actionImportPath = resolveSchemaActionImportPath(formName, options.outputNamespace)
  const adminRoutePath = options.adminRoutePath ?? '/admin'
  const devModeIntegration = buildFormDevModeIntegration(
    schema,
    options.adminDir ?? 'admin',
    actionImportPath,
    options.adminImportAlias
  )
  const fields = getAllFormSchemaFields(schema)
  const hasDelete = schema.actions?.delete !== false
  const hasExport = schema.actions?.export === true
  const actions = `${pascal}Actions`
  const deleteDialog = `Delete${pascal}SubmissionDialog`
  const deleteDialogFileName = `delete-${kebab}-submission-dialog`
  const baseFiles: GeneratedFile[] = [
    createGeneratedFile(`${pagesDir}/forms/${kebab}/page.tsx`, generatePage(pascal, kebab)),
    createGeneratedFile(
      `${pagesDir}/forms/${kebab}/${kebab}-submissions-page-skeleton.tsx`,
      generatePageSkeleton(pascal, schema.label)
    ),
    createGeneratedFile(
      `${pagesDir}/forms/${kebab}/columns.tsx`,
      generateColumns(
        fields,
        pascal,
        kebab,
        camel,
        actionImportPath,
        schema.columns,
        schema.actions
      )
    )
  ]
  if (hasDelete) {
    baseFiles.push(
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/actions.tsx`,
        generateFormRowActionsFile({
          actions,
          deleteDialog,
          deleteDialogFileName
        })
      ),
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${deleteDialogFileName}.tsx`,
        generateFormDeleteDialogFile({ pascal, camel, actionImportPath })
      )
    )
  }
  return {
    files: [
      ...baseFiles,
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${kebab}-submissions-table.tsx`,
        generateTable(pascal, kebab, schema.label, actionImportPath)
      ),
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${kebab}-submissions-page-content.tsx`,
        generatePageContent(
          pascal,
          kebab,
          camel,
          schema.label,
          actionImportPath,
          adminRoutePath,
          devModeIntegration.buttonExportName,
          devModeIntegration.buttonImportPath,
          hasExport,
          hasDelete
        )
      ),
      ...devModeIntegration.files,
      createGeneratedFile(
        `${pagesDir}/forms/${kebab}/${kebab}-submission-drawer.tsx`,
        generateSubmissionDrawer(
          pascal,
          kebab,
          fields,
          schema.label,
          hasDynamicFields(schema),
          actionImportPath
        )
      )
    ]
  }
}
